import React, { useEffect, useState } from "react";
import { adminService } from "../../services/adminService";

const defaultSlide = (order) => ({
  title: "",
  linkUrl: "",
  order,
  imageUrlSmall: "",
  imageUrlMedium: "",
  imageUrlLarge: "",
});

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const SlideshowAdmin = () => {
  const [slides, setSlides] = useState([defaultSlide(1)]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const data = await adminService.getAppSlideshow();
        if (!mounted) return;
        if (Array.isArray(data) && data.length > 0) {
          setSlides(
            data.map((s, idx) => ({
              title: s.title || "",
              linkUrl: s.linkUrl || "",
              order: idx + 1,
              imageUrlSmall: s.imageUrlSmall || s.imageUrl || "",
              imageUrlMedium: s.imageUrlMedium || s.imageUrl || s.imageUrlSmall || "",
              imageUrlLarge: s.imageUrlLarge || s.imageUrl || s.imageUrlMedium || s.imageUrlSmall || "",
            }))
          );
        }
      } catch (e) {
        console.error("Failed to load slideshow:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSlideChange = (idx, field, value) => {
    setSlides((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const addSlide = () => setSlides((prev) => [...prev, defaultSlide(prev.length + 1)]);

  const removeSlide = (idx) => {
    setSlides((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })));
  };

  const handleFileUpload = async (idx, field, file) => {
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      handleSlideChange(idx, field, dataUrl);
    } catch (e) {
      console.error("Image upload failed:", e);
      setMessage("Image upload failed. Please try a different image.");
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage("");
      const payload = {
        slides: slides.map((s, idx) => ({
          title: s.title || `Slide ${idx + 1}`,
          linkUrl: s.linkUrl || "",
          order: idx + 1,
          imageUrlSmall: s.imageUrlSmall || "",
          imageUrlMedium: s.imageUrlMedium || s.imageUrlSmall || "",
          imageUrlLarge: s.imageUrlLarge || s.imageUrlMedium || s.imageUrlSmall || "",
        })),
      };
      await adminService.updateAppSlideshow(payload);
      setMessage("Slideshow settings updated successfully.");
    } catch (e) {
      console.error("Failed to save slideshow:", e);
      setMessage("Failed to update slideshow. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings - Slideshow</h1>
        <p className="text-gray-600 mt-2">
          Upload responsive slideshow images for small, medium, and large screens used on web and mobile.
        </p>
      </div>

      {message && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md">{message}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          {slides.map((slide, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-gray-900">Slide {idx + 1}</p>
                <button
                  type="button"
                  onClick={() => removeSlide(idx)}
                  className="text-red-600 hover:text-red-800 font-medium"
                  disabled={slides.length <= 1}
                >
                  Remove
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={slide.title}
                    onChange={(e) => handleSlideChange(idx, "title", e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Slide title"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Link URL (optional)</label>
                  <input
                    type="url"
                    value={slide.linkUrl}
                    onChange={(e) => handleSlideChange(idx, "linkUrl", e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {[
                  { key: "imageUrlSmall", label: "Small Screen Image" },
                  { key: "imageUrlMedium", label: "Medium Screen Image" },
                  { key: "imageUrlLarge", label: "Large Screen Image" },
                ].map((imgField) => (
                  <div key={imgField.key} className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">{imgField.label}</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(idx, imgField.key, e.target.files?.[0])}
                      className="w-full text-sm"
                    />
                    {slide[imgField.key] ? (
                      <img
                        src={slide[imgField.key]}
                        alt={`${imgField.label} preview`}
                        className="w-full h-28 object-cover rounded-md border border-gray-200"
                      />
                    ) : (
                      <div className="w-full h-28 rounded-md border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-500">
                        No image selected
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={addSlide}
              className="bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium py-2 px-4 rounded-md"
            >
              Add Slide
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-md"
            >
              {saving ? "Saving..." : "Save Slideshow"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlideshowAdmin;

